import { config } from '@cfg';
import { Button } from '@components/Button/';
import PhoneInputField, { ErrorMessage, FormLabel } from '@components/FormFields';
import { AdvisorHeadshot } from '@components/MiscComponents/AdvisorHeadShot';
import { CreateLoftyLeadDto } from '@engelvoelkersamericas/api';
import { Close, Status } from '@icon/index';
import evAPI from '@util/api/ev';
import usePropertySaver from '@util/customHooks/usePropertySaver';
import { extractDomain } from '@util/getURL';
import handleRecaptchaAndLofty from '@util/recaptcha';
import { Checkbox } from 'antd';
import { usePathname, useRouter } from 'next/navigation';
import { FC, useEffect, useState } from 'react';

import { observer } from 'mobx-react-lite';
import { useStores } from 'mobx/useStores';
import { useTranslations, useLocale } from 'next-intl';
import {
  AdvisorHeadshotWrapper,
  AdvisorWrapper,
  AgentInfo,
  Box,
  BtnWrapper,
  CloseTrigger,
  Container,
  DisclaimerContainer,
  DisclaimerText,
  FormFinished,
  FormInner,
  FormMain,
  FormTrigger,
  FormWrapper,
  Header,
  Headline,
  InfoBlock,
  InputField,
  MessageField,
  SuccessBtnGroup,
  SuccessDescription,
  SVGWrapper,
  TwoColumnInputWrapper,
} from './styles';
import { sanitizedNumber } from '@util/phoneNumber';

export interface Advisor {
  shopId?: string;
  img?: string;
  name?: string;
  title?: string;
  office?: string;
  type?: string;
  chimeData?: {
    chimeId: number;
    type: string;
    ownershipId?: number;
    zipcode?: string;
    url?: string;
    welcomeEmail?: boolean;
  };
}

export interface ContactAdvisorProps {
  overrideText?: string;
  listAgentFullName?: string;
  listOfficeName?: string;
  originatingSystemName?: string;
  listAgentMlsId?: string;
  zipCode?: string;
  url?: string;
  listingId: string;
  listOfficeMlsId: string;
  isCooperativeAgency?: boolean;
  handleToggle(): void;
}

const ContactAdvisor: FC<ContactAdvisorProps> = observer(
  ({
    overrideText,
    listAgentFullName,
    listOfficeName,
    originatingSystemName,
    listAgentMlsId,
    zipCode,
    listingId,
    listOfficeMlsId,
    handleToggle,
    isCooperativeAgency,
  }) => {
    const locale = useLocale();
    const t = useTranslations();
    const router = useRouter();
    const pathname = usePathname();
    const { shopStore } = useStores();
    const { shopChimeId } = shopStore;

    const [isOpen, setIsOpen] = useState(false);
    const toggleForm = () => {
      setIsOpen(!isOpen);
      handleToggle();
    };

    const [isSent, setIsSent] = useState(false);
    const [isSuccess, setIsSuccess] = useState<boolean>(false);
    const [firstNameError, setFirstNameError] = useState<string | null>(null);
    const [lastNameError, setLastNameError] = useState<string | null>(null);
    const [phoneError, setPhoneError] = useState<string | null>(null);
    const [emailError, setEmailError] = useState<string | null>(null);
    const [messageError, setMessageError] = useState<string | null>(null);
    const [checkedBoxError, setCheckedBoxError] = useState<string | null>(null);
    const [recaptchaError, setRecaptchaError] = useState<string | null>(null);
    const [url, setURL] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const { isFav, onSaveProperty } = usePropertySaver({ listingId, listOfficeMlsId }); // Function for favoriting a property

    //
    //
    // Advisor Details
    //
    const api = new evAPI();
    const [advisorDetails, setAdvisorDetails] = useState<Advisor | null>(null);
    const getAdvisorData = async () => {
      if (!listAgentMlsId || !originatingSystemName) {
        return;
      }

      const isEVAdvisorCheck = listOfficeName?.toLowerCase()?.includes('engel & v');
      try {
        let advisorData;
        if (originatingSystemName === 'EVAMLS' && listAgentMlsId) {
          // if the originating system is EVAMLS, this is a manual listing and the listAgentMlsId will be the UUID for supplying to a different endpoint
          try {
            advisorData = await api.advisorBio(listAgentMlsId);
          } catch {}
        }
        if (!advisorData || !advisorData?.lastName) {
          const apiAgentArray = [{ osn: originatingSystemName, agentMlsId: listAgentMlsId }];
          const advisorDataQuery = await api.advisorDetailsOSNandMLSId(apiAgentArray);
          if (advisorDataQuery?.totalRecords > 0) {
            advisorData = advisorDataQuery?.records[0];
          }
        }

        let currentHost = typeof window !== 'undefined' ? window.location.host : '';
        if (
          currentHost.includes('localhost') ||
          currentHost.includes('evipscloud-uat.com') ||
          currentHost.includes('evipscloud-dev.com') ||
          currentHost.includes('dev.evadigitalexperience.com') ||
          currentHost.includes('uat.evadigitalexperience.com')
        ) {
          currentHost = currentHost.replace(extractDomain(currentHost), 'evrealestate.com');
        }
        let result;
        if (advisorData?.type?.toLowerCase() === 'team') {
          result = advisorData?.associatedShops?.find((aShop) => {
            return aShop?.domain?.toLowerCase() === currentHost.toLowerCase();
          });
        } else {
          result = advisorData?.accounts?.[0]?.associatedShops?.find((aShop) => {
            return aShop?.domain?.toLowerCase() === currentHost.toLowerCase();
          });
        }
        const isDomainExistInAssociatedShops = Boolean(result);

        let tmpChimeId;

        if (!isDomainExistInAssociatedShops || isCooperativeAgency) {
          tmpChimeId = shopChimeId;
        }
        if (!tmpChimeId) {
          if (advisorData?.type?.toLowerCase() === 'team') {
            tmpChimeId = advisorData?.chimeInfo?.chimeId;
          } else {
            tmpChimeId = advisorData?.accounts?.[0]?.chimeInfo?.teamMemberId;
          }
        }

        if (advisorData) {
          // if results come back
          const advisor = advisorData;
          const chimeType =
            advisor?.type?.toLowerCase() === 'team' || isCooperativeAgency ? 'Shop' : 'Agent';
          if (advisor?.type?.toLowerCase() === 'team') {
            setAdvisorDetails({
              shopId:
                advisor?.associatedShops && advisor?.associatedShops?.length > 0
                  ? advisor?.associatedShops[0].shopId
                  : '',
              img: advisor?.logoPicture,
              name: `${advisor?.name}`,
              title: advisor?.evTitle ? advisor?.evTitle : advisor?.title ? advisor?.title : '',
              office:
                advisor?.associatedShops && advisor?.associatedShops?.length > 0
                  ? advisor?.associatedShops.map((shop) => shop.name).join(', ')
                  : '',
              type: advisor?.type,
              chimeData: {
                chimeId: tmpChimeId,
                type: chimeType,
                welcomeEmail: isEVAdvisorCheck,
              },
            });
          } else {
            setAdvisorDetails({
              shopId:
                advisor?.accounts?.[0]?.associatedShops &&
                advisor?.accounts?.[0]?.associatedShops?.length > 0
                  ? advisor?.accounts?.[0]?.associatedShops[0].shopId
                  : '',
              img: advisor?.profileThumbnail,
              name: `${advisor?.name?.firstName} ${advisor?.name?.lastName}`,
              title: advisor?.evTitle ? advisor?.evTitle : advisor?.title ? advisor?.title : '',
              office:
                advisor?.accounts?.[0]?.associatedShops &&
                advisor?.accounts?.[0]?.associatedShops?.length > 0
                  ? advisor?.accounts?.[0]?.associatedShops?.[0]?.name
                  : '',
              type: advisor?.type,
              chimeData: {
                chimeId: tmpChimeId,
                type: chimeType,
                ownershipId: advisor?.accounts?.[0]?.associatedShops?.[0]?.chimeInfo?.chimeOfficeId,
                welcomeEmail: isEVAdvisorCheck,
              },
            });
          }
        } else if (isEVAdvisorCheck) {
          // if it's a EV advisor but no results came back
          setAdvisorDetails({
            img: '/assets/user_icon.png',
            name: listAgentFullName,
            title: 'Real Estate Advisor',
            office: listOfficeName,
            chimeData: {
              chimeId: 0,
              type: 'EV Global',
            },
          });
        } else {
          if (shopChimeId) {
            setAdvisorDetails({
              chimeData: {
                chimeId: shopChimeId,
                ownershipId: shopChimeId,
                type: 'Shop',
              },
            });
          } else {
            setAdvisorDetails(null);
          }
        }
      } catch (error) {
        console.log('advisor detail error', error);
      }
    };

    useEffect(() => {
      getAdvisorData();
    }, [originatingSystemName, listAgentMlsId, shopChimeId]);

    useEffect(() => {
      setURL(`${window.location.origin}${pathname}`);
    }, []);

    const sendForm = () => {
      setIsSent(true);
    };

    const handlePhoneInputChange = (e) => {
      setPhoneNumber(e ?? '');
    };
    const handleInputChange = (e) => {
      if (e?.target?.id == 'firstName') {
        setFirstNameError('');
      }

      if (e?.target?.id == 'lastName') {
        setLastNameError('');
      }

      if (e?.target?.id == 'email') {
        setEmailError('');
      }

      if (e?.target?.id == 'message') {
        setMessageError('');
      }

      if (e?.target?.id == 'checkedBox') {
        setCheckedBoxError('');
      }
    };
    const handleSubmit = async (event) => {
      // Stop the form from submitting and refreshing the page.
      event.preventDefault();
      setFirstNameError('');
      setLastNameError('');
      setEmailError('');
      setCheckedBoxError('');

      // Get data from the form.
      const data = {
        firstName: event.target.firstName.value,
        lastName: event.target.lastName.value,
        phone: event.target.phone.value,
        email: event.target.email.value,
        message: event.target.message.value,
        checkedBox: event.target.checkedBox.checked,
      };

      //validations
      let formIsValid = true;
      if (!data.firstName) {
        formIsValid = false;
        setFirstNameError('First Name cannot be empty');
      }

      if (!data.lastName) {
        formIsValid = false;
        setLastNameError('Last Name cannot be empty');
      }

      //Email
      if (!data.email) {
        formIsValid = false;
      }

      if (typeof data.email !== 'undefined') {
        let lastAtPos = data.email.lastIndexOf('@');
        let lastDotPos = data.email.lastIndexOf('.');

        if (
          !(
            lastAtPos < lastDotPos &&
            lastAtPos > 0 &&
            data.email.indexOf('@@') == -1 &&
            lastDotPos > 2 &&
            data.email.length - lastDotPos > 2
          )
        ) {
          formIsValid = false;
          setEmailError('Email is not valid');
        }
      }

      if (!data.checkedBox) {
        formIsValid = false;
        setCheckedBoxError('Please check to submit this form');
      }

      if (formIsValid) {
        setFirstNameError('');
        setLastNameError('');
        setEmailError('');
        setCheckedBoxError('');

        const chimeData: CreateLoftyLeadDto = {
          tags: advisorDetails?.shopId ?? '',
          userFirstName: data.firstName,
          userLastName: data.lastName,
          userPhone: sanitizedNumber(data.phone),
          userEmail: data.email,
          message: data.message,
          chimeId: advisorDetails?.chimeData?.chimeId ? advisorDetails?.chimeData?.chimeId : 0,
          type: advisorDetails?.chimeData?.type ? advisorDetails.chimeData.type : 'EV Global',
          ownershipId: advisorDetails?.chimeData?.ownershipId
            ? advisorDetails?.chimeData?.ownershipId
            : undefined,
          welcomeEmail: !!advisorDetails?.chimeData?.welcomeEmail,
        };
        if (advisorDetails?.type?.toLowerCase() === 'team') {
          chimeData.type = 'Shop';
        }
        // if url exists, add it
        if (url) {
          chimeData.url = url;
        }
        // if zip exists, add it
        if (zipCode) {
          chimeData.zipcode = zipCode;
        }

        if (listingId) {
          chimeData.listingId = listingId;
        }
        if (chimeData.ownershipId) {
          chimeData.ownershipScope = 'OFFICE';
        }
        await handleRecaptchaAndLofty(chimeData, sendForm, setRecaptchaError, setIsSuccess);
      }
    };

    // Function for save favorite property
    const handleSaveProperty = async () => {
      onSaveProperty();
      // close the form
      toggleForm();
    };

    return (
      <Container>
        <FormTrigger>
          <Button
            dataTestId="contact-advisor-button"
            arrow={false}
            variant={'primaryRed'}
            onClickHandler={() => toggleForm()}
            label={overrideText ? overrideText : t('Contact an Advisor')}
          />
        </FormTrigger>
        <form onSubmit={handleSubmit}>
          <FormWrapper $isOpen={isOpen}>
            <FormInner>
              <CloseTrigger onClick={toggleForm}>
                <Close />
              </CloseTrigger>
              {isSuccess == true && (
                <FormFinished>
                  <SVGWrapper>
                    <Status color="#2E8540" embeddedIcon="success" />
                  </SVGWrapper>
                  <Headline>{t(`ContactForm.successTitle`)}</Headline>
                  <SuccessDescription>{t(`ContactForm.successMessage`)}</SuccessDescription>
                  <SuccessBtnGroup>
                    {!isFav && (
                      <Button
                        variant="secondary"
                        onClickHandler={() => handleSaveProperty()}
                        label={t('Save This Home')}
                        arrow={false}
                      />
                    )}
                    <Button
                      variant="secondary"
                      onClickHandler={(event) => {
                        event.preventDefault();
                        router.back();
                      }}
                      label={t('Continue Search')}
                      arrow={false}
                    />
                  </SuccessBtnGroup>
                </FormFinished>
              )}
              {!isSuccess && (
                <>
                  <Header>{t('Contact an Advisor')}</Header>
                  {!isCooperativeAgency && advisorDetails && (
                    <AdvisorWrapper>
                      {advisorDetails.img && (
                        <AdvisorHeadshotWrapper>
                          <AdvisorHeadshot
                            img={advisorDetails.img}
                            alt_text={`${advisorDetails.name} ${t('Headshot')}`}
                          />
                        </AdvisorHeadshotWrapper>
                      )}
                      <AgentInfo>
                        <InfoBlock $bold={true}>{advisorDetails.name}</InfoBlock>
                        <InfoBlock>{advisorDetails.title}</InfoBlock>
                        <InfoBlock>{advisorDetails.office}</InfoBlock>
                      </AgentInfo>
                    </AdvisorWrapper>
                  )}
                  <FormMain>
                    <TwoColumnInputWrapper>
                      <Box>
                        <FormLabel>{t('First Name')}</FormLabel>
                        <InputField
                          id="firstName"
                          placeholder={t('First Name')}
                          onChange={(e) => handleInputChange(e)}
                          $isError={firstNameError != '' ? true : false}
                        />
                        {firstNameError && <ErrorMessage message={firstNameError} />}
                      </Box>
                      <Box>
                        <FormLabel>{t('Last Name')}</FormLabel>
                        <InputField
                          id="lastName"
                          placeholder={t('Last Name')}
                          onChange={(e) => handleInputChange(e)}
                          $isError={lastNameError != '' ? true : false}
                        />
                        {lastNameError && <ErrorMessage message={lastNameError} />}
                      </Box>
                    </TwoColumnInputWrapper>
                    <FormLabel>{t('Phone')}</FormLabel>
                    <PhoneInputField
                      id="phone"
                      locale={locale}
                      placeholder={'(123)-456-7890'}
                      isError={phoneError != '' ? true : false}
                      onChange={(e) => handlePhoneInputChange(e)}
                      value={phoneNumber}
                    />
                    {phoneError && <ErrorMessage message={phoneError} />}
                    <FormLabel>{t('Email')}</FormLabel>
                    <InputField
                      id="email"
                      placeholder={'you@emailaddress.com'}
                      onChange={(e) => handleInputChange(e)}
                      $isError={emailError != '' ? true : false}
                    />
                    {emailError && <ErrorMessage message={emailError} />}
                    <FormLabel>{t('Message')}</FormLabel>
                    <MessageField
                      id="message"
                      placeholder={t('contactMessagePlaceholder')}
                      onChange={(e) => handleInputChange(e)}
                    />
                    <DisclaimerContainer>
                      <Checkbox
                        id="checkedBox"
                        style={{ display: 'flex', alignSelf: 'start' }}
                        key="data-processing-consent"
                        title="data-processing-consent"
                      />
                      <DisclaimerText>{t('ContactForm.disclaimer')}</DisclaimerText>
                    </DisclaimerContainer>
                    {checkedBoxError && <ErrorMessage message={checkedBoxError} />}
                    {recaptchaError && (
                      <ErrorMessage
                        style={{ paddingTop: checkedBoxError ? '4px' : undefined }}
                        message={recaptchaError}
                      />
                    )}
                  </FormMain>
                </>
              )}
            </FormInner>
            {!isSuccess && (
              <BtnWrapper>
                <Button
                  type="submit"
                  className="g-recaptcha"
                  data-sitekey={config.recaptcha.key}
                  data-callback="onSubmit"
                  data-action="submit"
                  style={{
                    borderRadius: '4px',
                    padding: '12px 20px',
                    fontWeight: 500,
                    width: '100%',
                  }}
                  label={t('Send Message')}
                  variant={'secondaryRed'}
                  arrow={false}
                />
              </BtnWrapper>
            )}
          </FormWrapper>
        </form>
      </Container>
    );
  },
);

export default ContactAdvisor;
